<template>
    <div id="add-edit-operation-dialog-component">
        <v-dialog v-model="is_add_edit_operation_dialog_open" max-width="600px">
            <v-card>
                <v-card-title>
                    <span class="text-h5">{{ dialog_title }}</span>
                </v-card-title>
                <v-card-text class="pb-0">
                    <v-form ref="form">
                        <v-menu
                            v-model="menu_date_start"
                            :close-on-content-click="false"
                            min-width="auto"
                            offset-y
                            transition="scale-transition"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    :label="$t('date_operation_begin')"
                                    :value="formatDate(date_start)"
                                    filled
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker v-model="date_start" @input="menu_date_start = false" />
                        </v-menu>

                        <v-menu
                            v-model="menu_date_end"
                            :close-on-content-click="false"
                            min-width="auto"
                            offset-y
                            transition="scale-transition"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    :label="$t('date_operation_end')"
                                    :value="formatDate(date_end)"
                                    filled
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker v-model="date_end" @input="menu_date_end = false" />
                        </v-menu>

                        <v-text-field
                            id="operation-delta-volume"
                            v-model.number="operation.delta_volume"
                            :label="`${$t('Delta')} (L)`"
                            :rules="[rules.required_or_equal_zero]"
                            filled
                            min="0"
                            type="number"
                            @change="updateLastEditedField('delta_volume')"
                            @input="recalculateData('delta_volume')"
                        />

                        <v-text-field
                            id="operation-measure-before"
                            v-model.number="operation.measure_before_operation"
                            :label="`${$t('use_chart.measure_before_operations')} (L)`"
                            :rules="[rules.required_or_equal_zero]"
                            filled
                            min="0"
                            type="number"
                            @change="updateLastEditedField('measure_before_operation')"
                            @input="recalculateData('measure_before_operation')"
                        />

                        <v-text-field
                            id="operation-measure-after"
                            v-model.number="operation.measure_after_operation"
                            :label="`${$t('use_chart.measure_after_operations')} (L)`"
                            :rules="[rules.required_or_equal_zero]"
                            filled
                            min="0"
                            type="number"
                            @change="updateLastEditedField('measure_after_operation')"
                            @input="recalculateData('measure_after_operation')"
                        />

                        <v-text-field
                            v-model.number="operation.price_per_hundred_liters"
                            :label="`${$t('asset_operations_datatable_widget_component.price_per_100_liters')} (€/100L)`"
                            :rules="[rules.required_or_equal_zero]"
                            filled
                            min="0"
                            type="number"
                        />

                        <v-select
                            v-model="operation.id_ref_intervention"
                            :items="operations_list"
                            :label="$t('asset_operations_datatable_widget_component.operation_type')"
                            :menu-props="{ bottom: true, offsetY: true }"
                            :rules="[rules.required]"
                            filled
                            item-text="text"
                            item-value="value"
                        />
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="is_add_edit_operation_dialog_open = false">{{ $t('global.cancel') }}</v-btn>
                    <v-btn :loading="updating_operation" color="primary" elevation="0" rounded @click="prepareAddEditOperation">
                        <span class="white--text">{{ $t('global.validate') }}</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import moment from 'moment'
import AssetRepository from '@/repositories/AssetRepository'

export default {
    name: 'AddEditOperationDialogComponent',
    props: {
        asset: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            is_add_edit_operation_dialog_open: false,
            is_new: false,
            updating_operation: false,
            menu_date_start: false,
            menu_date_end: false,
            date_start: null,
            date_end: null,
            last_edited_field: '', // last field modified by the user, arbitrates which value recalculateData recomputes
            operation: {},
            operations_list: [
                { text: this.$t('key_operation.tank_fill'), value: 140 },
                { text: this.$t('key_operation.tank_flush'), value: 141 },
                { text: this.$t('key_operation.tank_volume_price'), value: 297 },
            ],
            rules: {
                required: (value) => !!value || this.$t('manage_equipment_component.required_field'),
                required_or_equal_zero: (value) => value === 0 || !!value || this.$t('manage_equipment_component.required_field'),
            },
        }
    },
    computed: {
        dialog_title() {
            return this.is_new
                ? this.$t('asset_operations_datatable_widget_component.add_operation')
                : this.$t('asset_operations_datatable_widget_component.edit_operation')
        },
    },
    methods: {
        openAddOperationDialog() {
            this.last_edited_field = ''
            this.is_new = true
            this.operation = {
                id_ref_intervention: 140, // tank_fill referential
                delta_volume: 0,
                measure_before_operation: 0,
                measure_after_operation: 0,
                price_per_hundred_liters: 0,
            }
            this.date_start = moment().format('YYYY-MM-DD')
            this.date_end = moment().format('YYYY-MM-DD')
            this.is_add_edit_operation_dialog_open = true
        },
        openEditOperationDialog(operation) {
            this.is_new = false
            this.last_edited_field = ''
            this.operation = JSON.parse(JSON.stringify(operation))
            this.date_start = moment(this.operation.date_operation_start).format('YYYY-MM-DD')
            this.date_end = moment(this.operation.date_operation_end ?? this.operation.date_operation_start).format('YYYY-MM-DD')
            this.is_add_edit_operation_dialog_open = true
        },
        formatDate(date) {
            return date ? moment(date).format(this.date_format) : ''
        },
        updateLastEditedField(field) {
            this.last_edited_field = field
        },
        recalculateData(field) {
            if (field === 'delta_volume') {
                if (this.last_edited_field === 'measure_after_operation') {
                    this.operation.measure_before_operation = Math.max(
                        this.operation.measure_after_operation - this.operation.delta_volume,
                        0
                    )
                } else {
                    this.operation.measure_after_operation = this.operation.measure_before_operation + this.operation.delta_volume
                }
            } else if (this.last_edited_field === 'delta_volume') {
                if (field === 'measure_after_operation') {
                    this.operation.measure_before_operation = Math.max(
                        this.operation.measure_after_operation - this.operation.delta_volume,
                        0
                    )
                } else {
                    this.operation.measure_after_operation = this.operation.measure_before_operation + this.operation.delta_volume
                }
            } else {
                this.operation.delta_volume = Math.max(this.operation.measure_after_operation - this.operation.measure_before_operation, 0)
            }
        },
        prepareAddEditOperation() {
            if (this.$refs.form.validate()) {
                this.updating_operation = true
                this.operation.date_operation_start = this.keepTimeOfOperationDate(this.date_start, this.operation.date_operation_start)
                this.operation.date_operation_end = this.keepTimeOfOperationDate(this.date_end, this.operation.date_operation_end)
                const request = this.is_new
                    ? AssetRepository.postAddOperation(this.asset.id_asset, this.operation)
                    : AssetRepository.putUpdateOperation(this.operation)
                const message = this.is_new ? this.$t('dialog_manage_widget_component.add_success') : this.$t('global.updated')
                request
                    .then(() => {
                        this.is_add_edit_operation_dialog_open = false
                        this.showSnackbar('success', message)
                        this.$emit(this.is_new ? 'added:operation' : 'updated:operation')
                    })
                    .catch((error) => {
                        this.manageError(error)
                    })
                    .finally(() => {
                        this.updating_operation = false
                    })
            }
        },
        keepTimeOfOperationDate(date, operationDate) {
            const time = operationDate ? moment(operationDate).format('HH:mm:ss') : '00:00:00'

            return `${moment(date).format('YYYY-MM-DD')} ${time}`
        },
    },
}
</script>
